// src/views/vault/crm/vault_crm.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { api, usersAPI } from 'services/api'
import { useAuth } from 'context/AuthContext'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    const first = parts[0]?.[0] || ''
    const last  = parts[parts.length - 1]?.[0] || ''
    return `${first}${last}`.toUpperCase()
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    try {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit', month: 'long', year: 'numeric',
        })
    } catch { return dateString }
}

const formatDateShort = (dateString) => {
    if (!dateString) return '-'
    try {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit', month: 'short',
        })
    } catch { return dateString }
}

const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0)

// NoteListRenderer — satır satır bullet liste
const NoteListRenderer = ({ text, emptyMessage = 'Not eklenmemiş...' }) => {
    if (!text || text.trim() === '') return <em className="crm-note-empty">{emptyMessage}</em>
    const lines = text.split('\n').filter(l => l.trim() !== '')
    if (lines.length === 0) return <em className="crm-note-empty">{emptyMessage}</em>
    return (
        <ul className="crm-note-list">
            {lines.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
    )
}

// ─── INLINE ICONS ─────────────────────────────────────────────────────────────
const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
)
const UserIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
)
const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6"/><line x1="9" y1="12" x2="21" y2="12"/>
    </svg>
)
const CalendarIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
)
const MessageIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
)
const PencilIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
)
const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
)
const TrashIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6"/><path d="M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
)
const CheckIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
    </svg>
)
const XIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
)
const WarnIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
)
const StarIcon = ({ filled }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled?"currentColor":"none"} stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
)
const SpinnerIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         style={{ animation: 'crm-spin 0.8s linear infinite', display:'inline-block' }}>
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.2"/>
        <path d="M21 12a9 9 0 00-9-9"/>
    </svg>
)
const PhoneIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
)
const TrendUpIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
    </svg>
)
const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
    </svg>
)
const ChevronLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
)
const ChevronRightIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
)

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .crm-page {
      font-family: 'Plus Jakarta Sans', sans-serif;
      --crm-accent:     #7c5cfc;
      --crm-accent-l:   rgba(124,92,252,0.12);
      --crm-accent-h:   #6b4edb;
      --crm-success:    #12b886;
      --crm-success-l:  rgba(18,184,134,0.12);
      --crm-error:      #fa5252;
      --crm-error-l:    rgba(250,82,82,0.12);
      --crm-warn:       #f59e0b;
      --crm-warn-l:     rgba(245,158,11,0.12);
      --crm-info:       #3b82f6;
      --crm-info-l:     rgba(59,130,246,0.12);

      /* light */
      --crm-bg:         #f6f5ff;
      --crm-card:       #ffffff;
      --crm-card2:      #f2f0ff;
      --crm-border:     rgba(124,92,252,0.12);
      --crm-border2:    rgba(0,0,0,0.07);
      --crm-text1:      #1a1a2e;
      --crm-text2:      #6b7280;
      --crm-text3:      #9ca3af;
      --crm-shadow:     0 4px 24px rgba(124,92,252,0.07);
      --crm-shadow-lg:  0 12px 40px rgba(124,92,252,0.13);
      --crm-skel1:      rgba(124,92,252,0.08);
      --crm-skel2:      rgba(124,92,252,0.16);
      --crm-tl-line:    rgba(124,92,252,0.18);

      /* gradient profile card */
      --crm-grad-start: #7c5cfc;
      --crm-grad-end:   #a855f7;
    }

    /* ── DARK MODE ── */
    [data-coreui-theme="dark"] .crm-page,
    [data-bs-theme="dark"] .crm-page,
    .dark .crm-page {
      --crm-bg:         #0f0e17;
      --crm-card:       #1a1928;
      --crm-card2:      #1f1e2e;
      --crm-border:     rgba(124,92,252,0.2);
      --crm-border2:    rgba(255,255,255,0.06);
      --crm-text1:      #f0eeff;
      --crm-text2:      #a09bc0;
      --crm-text3:      #6b6785;
      --crm-shadow:     0 4px 24px rgba(0,0,0,0.35);
      --crm-shadow-lg:  0 12px 40px rgba(0,0,0,0.5);
      --crm-skel1:      rgba(124,92,252,0.08);
      --crm-skel2:      rgba(124,92,252,0.18);
      --crm-tl-line:    rgba(124,92,252,0.25);
    }

    /* ── ANIMATIONS ── */
    @keyframes crm-spin  { to { transform: rotate(360deg); } }
    @keyframes crm-shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes crm-fade-in {
      from { opacity:0; transform:translateY(12px); }
      to   { opacity:1; transform:none; }
    }
    @keyframes crm-modal-in {
      from { opacity:0; transform:translateY(-16px) scale(0.97); }
      to   { opacity:1; transform:none; }
    }
    @keyframes crm-pop {
      0%   { transform: scale(0.8); opacity:0; }
      60%  { transform: scale(1.08); }
      100% { transform: scale(1); opacity:1; }
    }

    /* ── BASE ── */
    .crm-page { background: var(--crm-bg); min-height: 100vh; }

    /* ── SKELETON ── */
    .crm-skeleton {
      background: linear-gradient(90deg,var(--crm-skel1) 25%,var(--crm-skel2) 50%,var(--crm-skel1) 75%);
      background-size: 200% 100%;
      animation: crm-shimmer 1.5s infinite;
      border-radius: 8px;
    }

    /* ── ALERT ── */
    .crm-alert {
      display:flex; align-items:flex-start; gap:10px;
      padding:12px 16px; border-radius:12px;
      font-size:0.85rem; font-weight:500;
      margin-bottom:16px; animation: crm-fade-in 0.2s ease;
    }
    .crm-alert-success { background:var(--crm-success-l); color:var(--crm-success); border:1px solid rgba(18,184,134,0.25); }
    .crm-alert-danger  { background:var(--crm-error-l);   color:var(--crm-error);   border:1px solid rgba(250,82,82,0.25); }
    .crm-alert-warn    { background:var(--crm-warn-l);    color:var(--crm-warn);    border:1px solid rgba(245,158,11,0.25); }
    .crm-alert-info    { background:var(--crm-info-l);    color:var(--crm-info);    border:1px solid rgba(59,130,246,0.25); }
    .crm-alert-close { background:none; border:none; cursor:pointer; color:inherit; opacity:0.6; padding:0; margin-left:auto; flex-shrink:0; }
    .crm-alert-close:hover { opacity:1; }

    /* ── HEADER ── */
    .crm-header-card {
      background:var(--crm-card); border-radius:20px;
      border:1px solid var(--crm-border); box-shadow:var(--crm-shadow);
      padding:20px 24px; margin-bottom:16px;
      display:flex; align-items:center; justify-content:space-between;
      flex-wrap:wrap; gap:14px;
    }
    .crm-header-left { display:flex; align-items:center; gap:14px; }
    .crm-header-icon {
      width:48px; height:48px; border-radius:14px;
      background:var(--crm-accent-l); color:var(--crm-accent);
      display:flex; align-items:center; justify-content:center; flex-shrink:0;
    }
    .crm-header-title { font-size:1.15rem; font-weight:800; color:var(--crm-text1); margin:0; }
    .crm-header-sub   { font-size:0.78rem; color:var(--crm-text3); margin:2px 0 0; }

    /* ── STATS ROW ── */
    .crm-stats-row {
      display:grid; gap:12px;
      grid-template-columns:repeat(2,1fr);
      margin-bottom:16px;
    }
    @media(min-width:640px) { .crm-stats-row { grid-template-columns:repeat(4,1fr); } }
    .crm-stat-card {
      background:var(--crm-card); border-radius:16px;
      border:1px solid var(--crm-border); box-shadow:var(--crm-shadow);
      padding:14px 16px; display:flex; align-items:center; gap:12px;
    }
    .crm-stat-icon {
      width:40px; height:40px; border-radius:12px; flex-shrink:0;
      display:flex; align-items:center; justify-content:center;
    }
    .crm-stat-label { font-size:0.68rem; font-weight:700; color:var(--crm-text3); text-transform:uppercase; letter-spacing:0.5px; margin:0; }
    .crm-stat-value { font-size:1.2rem; font-weight:800; color:var(--crm-text1); margin:0; line-height:1.1; }

    /* ── MAIN CARD ── */
    .crm-main-card {
      background:var(--crm-card); border-radius:20px;
      border:1px solid var(--crm-border); box-shadow:var(--crm-shadow);
      overflow:hidden;
    }

    /* ── TOOLBAR ── */
    .crm-toolbar {
      padding:16px 20px; border-bottom:1px solid var(--crm-border2);
      display:flex; gap:10px; flex-wrap:wrap; align-items:center;
    }
    .crm-search-wrap { position:relative; flex:1; min-width:180px; }
    .crm-search-wrap svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--crm-text3); pointer-events:none; }
    .crm-search {
      width:100%; padding:9px 12px 9px 36px;
      background:var(--crm-card2); border:1.5px solid var(--crm-border);
      border-radius:10px; color:var(--crm-text1);
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.82rem; outline:none;
      transition:border-color 0.2s;
    }
    .crm-search:focus   { border-color:var(--crm-accent); }
    .crm-search::placeholder { color:var(--crm-text3); }
    .crm-result-count { font-size:0.75rem; color:var(--crm-text3); font-weight:500; white-space:nowrap; }

    /* ── CUSTOMER GRID ── */
    .crm-customer-grid {
      display:grid; gap:12px; padding:16px 20px;
      grid-template-columns:1fr;
    }
    @media(min-width:480px) { .crm-customer-grid { grid-template-columns:repeat(2,1fr); } }
    @media(min-width:768px) { .crm-customer-grid { grid-template-columns:repeat(3,1fr); } }
    @media(min-width:1200px){ .crm-customer-grid { grid-template-columns:repeat(4,1fr); } }

    /* ── CUSTOMER CARD ── */
    .crm-customer-card {
      background:var(--crm-card2); border-radius:18px;
      border:2px solid transparent; cursor:pointer;
      transition:all 0.22s ease; padding:20px 16px;
      display:flex; flex-direction:column; align-items:center; text-align:center;
      position:relative; overflow:hidden;
      animation: crm-fade-in 0.3s ease both;
    }
    .crm-customer-card::before {
      content:''; position:absolute; inset:0;
      background:linear-gradient(135deg,var(--crm-accent-l),transparent 60%);
      opacity:0; transition:opacity 0.2s;
    }
    .crm-customer-card:hover { border-color:var(--crm-accent); transform:translateY(-4px); box-shadow:0 8px 28px rgba(124,92,252,0.18); }
    .crm-customer-card:hover::before { opacity:1; }
    .crm-avatar {
      width:64px; height:64px; border-radius:50%; flex-shrink:0;
      display:flex; align-items:center; justify-content:center;
      font-size:1.1rem; font-weight:800; margin-bottom:12px;
      position:relative; z-index:1;
    }
    .crm-avatar-ring {
      position:absolute; inset:-3px; border-radius:50%;
      border:2.5px solid var(--crm-accent);
      opacity:0; transition:opacity 0.2s;
    }
    .crm-customer-card:hover .crm-avatar-ring { opacity:1; }
    .crm-customer-name { font-size:0.88rem; font-weight:800; color:var(--crm-text1); margin:0 0 4px; position:relative; z-index:1; }
    .crm-customer-phone { font-size:0.73rem; color:var(--crm-text3); font-weight:500; display:flex; align-items:center; gap:4px; position:relative; z-index:1; }
    .crm-customer-tag {
      margin-top:8px; font-size:0.65rem; font-weight:700;
      padding:3px 10px; border-radius:20px;
      background:var(--crm-accent-l); color:var(--crm-accent);
      position:relative; z-index:1;
    }

    /* ── SKELETON CARDS ── */
    .crm-skel-card {
      background:var(--crm-card2); border-radius:18px;
      border:2px solid var(--crm-border); padding:20px 16px;
      display:flex; flex-direction:column; align-items:center; gap:10px;
    }

    /* ── PAGINATION ── */
    .crm-pagination {
      display:flex; align-items:center; justify-content:center; gap:4px;
      padding:16px 20px; border-top:1px solid var(--crm-border2);
    }
    .crm-page-btn {
      min-width:34px; height:34px; padding:0 8px; border-radius:9px;
      display:inline-flex; align-items:center; justify-content:center;
      border:1.5px solid var(--crm-border); background:var(--crm-card2);
      color:var(--crm-text2); font-family:'Plus Jakarta Sans',sans-serif;
      font-size:0.78rem; font-weight:700; cursor:pointer; transition:all 0.15s;
    }
    .crm-page-btn:hover:not(:disabled):not(.active) { border-color:var(--crm-accent); color:var(--crm-accent); background:var(--crm-accent-l); }
    .crm-page-btn.active { background:var(--crm-accent); border-color:var(--crm-accent); color:#fff; }
    .crm-page-btn:disabled { opacity:0.35; cursor:not-allowed; }

    /* ── EMPTY STATE ── */
    .crm-empty {
      display:flex; flex-direction:column; align-items:center;
      padding:56px 24px; text-align:center; color:var(--crm-text3);
    }
    .crm-empty-icon {
      width:72px; height:72px; border-radius:22px;
      background:var(--crm-accent-l); color:var(--crm-accent);
      display:flex; align-items:center; justify-content:center; margin-bottom:16px;
    }
    .crm-empty-title { font-size:1rem; font-weight:800; color:var(--crm-text1); margin:0 0 6px; }
    .crm-empty-sub   { font-size:0.82rem; color:var(--crm-text3); margin:0; }

    /* ═══════════════════════════════════════════════════════════════
       DETAIL VIEW
    ═══════════════════════════════════════════════════════════════ */

    /* ── BACK BUTTON ── */
    .crm-back-btn {
      display:inline-flex; align-items:center; gap:8px;
      padding:9px 16px; border-radius:11px;
      background:var(--crm-card); border:1.5px solid var(--crm-border);
      color:var(--crm-text2); font-family:'Plus Jakarta Sans',sans-serif;
      font-size:0.82rem; font-weight:700; cursor:pointer;
      transition:all 0.18s; margin-bottom:16px;
    }
    .crm-back-btn:hover { border-color:var(--crm-accent); color:var(--crm-accent); background:var(--crm-accent-l); }

    /* ── PROFILE HERO CARD ── */
    .crm-profile-hero {
      background:linear-gradient(135deg,var(--crm-grad-start),var(--crm-grad-end));
      border-radius:24px; padding:28px 28px 24px;
      margin-bottom:16px;
      box-shadow:0 12px 40px rgba(124,92,252,0.3);
      position:relative; overflow:hidden;
      animation: crm-fade-in 0.35s ease;
    }
    .crm-profile-hero::before {
      content:''; position:absolute; top:-60px; right:-60px;
      width:200px; height:200px; border-radius:50%;
      background:rgba(255,255,255,0.06);
    }
    .crm-profile-hero::after {
      content:''; position:absolute; bottom:-40px; left:30%;
      width:150px; height:150px; border-radius:50%;
      background:rgba(255,255,255,0.04);
    }
    .crm-profile-hero-inner {
      display:flex; gap:20px; flex-wrap:wrap; align-items:flex-start;
      position:relative; z-index:1;
    }
    .crm-profile-avatar {
      width:90px; height:90px; border-radius:50%; flex-shrink:0;
      background:rgba(255,255,255,0.2); color:#fff;
      display:flex; align-items:center; justify-content:center;
      font-size:1.8rem; font-weight:800;
      border:3px solid rgba(255,255,255,0.3);
      box-shadow:0 8px 24px rgba(0,0,0,0.2);
    }
    .crm-profile-info { flex:1; min-width:180px; }
    .crm-profile-name { font-size:1.5rem; font-weight:800; color:#fff; margin:0 0 4px; }
    .crm-profile-phone { font-size:0.85rem; color:rgba(255,255,255,0.7); display:flex; align-items:center; gap:5px; margin:0 0 14px; }
    .crm-profile-notes-label {
      font-size:0.65rem; font-weight:700; letter-spacing:0.8px;
      color:rgba(255,255,255,0.55); text-transform:uppercase; margin:0 0 6px;
    }
    .crm-profile-edit-btn {
      background:rgba(255,255,255,0.15); border:1.5px solid rgba(255,255,255,0.25);
      border-radius:10px; padding:8px 10px; cursor:pointer; color:#fff;
      transition:all 0.18s; flex-shrink:0; display:flex; align-items:center;
    }
    .crm-profile-edit-btn:hover { background:rgba(255,255,255,0.25); }

    /* ── STATS MINI (on detail) ── */
    .crm-detail-stats {
      display:grid; gap:10px; margin-bottom:16px;
      grid-template-columns:repeat(2,1fr);
    }
    @media(min-width:640px) { .crm-detail-stats { grid-template-columns:repeat(4,1fr); } }
    .crm-detail-stat {
      background:var(--crm-card); border-radius:14px;
      border:1px solid var(--crm-border); padding:14px 16px;
      animation: crm-pop 0.3s ease both;
    }
    .crm-detail-stat-label { font-size:0.65rem; font-weight:700; color:var(--crm-text3); text-transform:uppercase; letter-spacing:0.5px; margin:0 0 4px; }
    .crm-detail-stat-val   { font-size:1.15rem; font-weight:800; color:var(--crm-text1); margin:0; }

    /* ── SECTION TITLE ── */
    .crm-section-title {
      display:flex; align-items:center; gap:10px;
      font-size:1rem; font-weight:800; color:var(--crm-text1);
      margin:24px 0 14px;
    }
    .crm-section-title-icon {
      width:30px; height:30px; border-radius:8px;
      background:var(--crm-accent-l); color:var(--crm-accent);
      display:flex; align-items:center; justify-content:center; flex-shrink:0;
    }

    /* ── TIMELINE ── */
    .crm-timeline { position:relative; padding:4px 0; }
    .crm-timeline::before {
      content:''; position:absolute; left:22px; top:0; bottom:0;
      width:3px; background:var(--crm-tl-line); border-radius:2px;
    }
    .crm-timeline-item {
      position:relative; padding-left:54px; margin-bottom:20px;
      animation: crm-fade-in 0.3s ease both;
    }
    .crm-timeline-dot {
      position:absolute; left:0; top:0; width:44px; height:44px;
      border-radius:50%; background:var(--crm-accent);
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      font-size:0.6rem; font-weight:800; color:#fff; text-align:center; line-height:1.1;
      border:3px solid var(--crm-card); z-index:1;
      box-shadow:0 4px 12px rgba(124,92,252,0.35);
    }
    .crm-timeline-dot-day   { font-size:0.9rem; font-weight:900; }
    .crm-timeline-dot-month { font-size:0.55rem; font-weight:700; text-transform:uppercase; opacity:0.85; }

    /* ── APPOINTMENT CARD ── */
    .crm-appt-card {
      background:var(--crm-card); border-radius:18px;
      border:1px solid var(--crm-border); overflow:hidden;
      box-shadow:var(--crm-shadow);
    }
    .crm-appt-header {
      padding:14px 18px; display:flex; align-items:center; justify-content:space-between;
      border-bottom:1px solid var(--crm-border2); gap:10px;
    }
    .crm-appt-service { font-size:0.95rem; font-weight:800; color:var(--crm-text1); margin:0 0 2px; }
    .crm-appt-price   { font-size:0.82rem; font-weight:700; color:var(--crm-success); }
    .crm-badge-done   { background:var(--crm-success-l); color:var(--crm-success); padding:4px 12px; border-radius:20px; font-size:0.68rem; font-weight:700; white-space:nowrap; }
    .crm-appt-body    { padding:14px 18px; }
    .crm-appt-footer  { padding:10px 18px; border-top:1px solid var(--crm-border2); text-align:center; }

    /* ── NOTE ITEM ── */
    .crm-note-item {
      display:flex; align-items:flex-start; gap:12px;
      padding:10px 14px; border-radius:12px;
      background:var(--crm-card2); border:1px solid var(--crm-border2);
      margin-bottom:8px; transition:border-color 0.2s;
    }
    .crm-note-item:last-child { margin-bottom:0; }
    .crm-note-item:hover { border-color:var(--crm-border); }
    .crm-note-content { flex:1; min-width:0; }
    .crm-note-staff { font-size:0.68rem; font-weight:700; color:var(--crm-text3); display:flex; align-items:center; gap:4px; margin-top:5px; }
    .crm-note-actions { display:flex; gap:4px; flex-shrink:0; }
    .crm-note-empty { font-size:0.8rem; color:var(--crm-text3); font-style:italic; }
    .crm-note-list { list-style:disc; padding-left:18px; margin:0; font-size:0.85rem; color:var(--crm-text1); }
    .crm-note-list li { margin-bottom:4px; line-height:1.5; }

    /* ── NOTE EDIT MODE ── */
    .crm-note-edit { width:100%; }
    .crm-note-input {
      width:100%; padding:8px 12px;
      background:var(--crm-card2); border:1.5px solid var(--crm-accent);
      border-radius:8px; color:var(--crm-text1);
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.85rem; outline:none;
      margin-bottom:8px; box-sizing:border-box;
    }
    .crm-note-edit-btns { display:flex; gap:6px; }

    /* ── NO NOTES ── */
    .crm-no-notes {
      display:flex; align-items:center; justify-content:center; gap:8px;
      padding:18px; background:var(--crm-card2); border-radius:12px;
      font-size:0.82rem; color:var(--crm-text3); font-style:italic;
    }

    /* ── ADD NOTE BTN ── */
    .crm-add-note-btn {
      display:inline-flex; align-items:center; gap:6px;
      padding:7px 16px; border-radius:9px;
      background:var(--crm-accent-l); border:1.5px solid var(--crm-accent);
      color:var(--crm-accent); font-family:'Plus Jakarta Sans',sans-serif;
      font-size:0.78rem; font-weight:700; cursor:pointer; transition:all 0.18s;
    }
    .crm-add-note-btn:hover { background:var(--crm-accent); color:#fff; }

    /* ── DETAIL LOADING ── */
    .crm-detail-loader {
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      min-height:300px; gap:14px;
    }
    .crm-detail-spinner {
      width:48px; height:48px; border-radius:50%;
      border:3px solid var(--crm-accent-l); border-top-color:var(--crm-accent);
      animation:crm-spin 0.8s linear infinite;
    }
    .crm-detail-spinner-text { font-size:0.85rem; font-weight:600; color:var(--crm-text3); }

    /* ── BUTTONS ── */
    .crm-btn {
      display:inline-flex; align-items:center; justify-content:center; gap:6px;
      padding:9px 18px; border-radius:11px;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.82rem; font-weight:700;
      cursor:pointer; transition:all 0.18s; border:none; white-space:nowrap;
    }
    .crm-btn-primary { background:var(--crm-accent); color:#fff; }
    .crm-btn-primary:hover:not(:disabled) { background:var(--crm-accent-h); box-shadow:0 4px 14px rgba(124,92,252,0.3); transform:translateY(-1px); }
    .crm-btn-ghost   { background:var(--crm-card2); color:var(--crm-text2); border:1.5px solid var(--crm-border); }
    .crm-btn-ghost:hover:not(:disabled) { border-color:var(--crm-accent); color:var(--crm-accent); }
    .crm-btn-success { background:var(--crm-success); color:#fff; }
    .crm-btn-success:hover:not(:disabled) { background:#0ca678; }
    .crm-btn-danger  { background:var(--crm-error); color:#fff; }
    .crm-btn-danger:hover:not(:disabled)  { background:#e03131; }
    .crm-btn:disabled { opacity:0.6; cursor:not-allowed; transform:none !important; box-shadow:none !important; }
    .crm-btn-sm { padding:5px 12px; font-size:0.75rem; border-radius:8px; }
    .crm-btn-icon {
      width:30px; height:30px; padding:0; border-radius:8px;
      background:var(--crm-card2); border:1.5px solid var(--crm-border);
      color:var(--crm-text3); display:inline-flex; align-items:center; justify-content:center;
      cursor:pointer; transition:all 0.15s;
    }
    .crm-btn-icon:hover { border-color:var(--crm-accent); color:var(--crm-accent); background:var(--crm-accent-l); }
    .crm-btn-icon.del:hover { border-color:var(--crm-error); color:var(--crm-error); background:var(--crm-error-l); }

    /* ── MODAL ── */
    .crm-modal-overlay {
      position:fixed; inset:0;
      background:rgba(0,0,0,0.55); backdrop-filter:blur(4px);
      z-index:1050; display:flex; align-items:center; justify-content:center;
      padding:16px;
    }
    .crm-modal {
      background:var(--crm-card); border-radius:20px;
      border:1px solid var(--crm-border); box-shadow:var(--crm-shadow-lg);
      width:100%; max-width:520px; max-height:90vh;
      display:flex; flex-direction:column;
      animation:crm-modal-in 0.2s ease both;
    }
    .crm-modal-header {
      padding:18px 22px; border-bottom:1px solid var(--crm-border2);
      display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
    }
    .crm-modal-title { font-size:1rem; font-weight:800; color:var(--crm-text1); }
    .crm-modal-close {
      width:32px; height:32px; border-radius:8px;
      background:var(--crm-card2); border:none; cursor:pointer;
      color:var(--crm-text3); display:flex; align-items:center; justify-content:center;
      transition:all 0.15s;
    }
    .crm-modal-close:hover { background:var(--crm-error-l); color:var(--crm-error); }
    .crm-modal-body   { padding:22px; overflow-y:auto; flex:1; }
    .crm-modal-footer { padding:14px 22px; border-top:1px solid var(--crm-border2); display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }

    /* ── MODAL FORM ── */
    .crm-form-label { display:block; margin-bottom:7px; font-size:0.7rem; font-weight:700; letter-spacing:0.6px; color:var(--crm-text3); text-transform:uppercase; }
    .crm-textarea {
      width:100%; padding:11px 13px;
      background:var(--crm-card2); border:1.5px solid var(--crm-border);
      border-radius:12px; color:var(--crm-text1);
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.85rem; outline:none;
      transition:border-color 0.2s; resize:none; box-sizing:border-box;
    }
    .crm-textarea:focus { border-color:var(--crm-accent); }
    .crm-textarea::placeholder { color:var(--crm-text3); }
    .crm-helper-text { font-size:0.72rem; color:var(--crm-text3); margin-top:6px; display:flex; align-items:center; gap:5px; }

    /* ── MODAL APPOINTMENT INFO ── */
    .crm-modal-appt-info {
      background:var(--crm-info-l); border:1px solid rgba(59,130,246,0.2);
      border-radius:12px; padding:12px 16px;
      display:flex; align-items:center; gap:12px; margin-bottom:16px;
      font-size:0.85rem;
    }
    .crm-modal-appt-icon { color:var(--crm-info); flex-shrink:0; }
    .crm-modal-appt-name { font-weight:800; color:var(--crm-text1); }
    .crm-modal-appt-date { font-size:0.75rem; color:var(--crm-text3); }
  `}</style>
)

// ─── AVATAR COLOR POOL ────────────────────────────────────────────────────────
const AVATAR_COLORS = [
    ['#7c5cfc','#f0eeff'],['#12b886','#e6faf4'],['#f59e0b','#fef3cd'],
    ['#3b82f6','#dbeafe'],['#ec4899','#fce7f3'],['#8b5cf6','#ede9fe'],
    ['#06b6d4','#cffafe'],['#ef4444','#fee2e2'],
]
const getAvatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0]
    const idx = name.charCodeAt(0) % AVATAR_COLORS.length
    return AVATAR_COLORS[idx]
}

// ─── CUSTOMER AVATAR ─────────────────────────────────────────────────────────
const CustomerAvatar = ({ name, size = 64, fontSize = '1.1rem' }) => {
    const [bg, fg] = getAvatarColor(name)
    return (
        <div className="crm-avatar" style={{ width:size, height:size, background:bg, color:fg, fontSize }}>
            {getInitials(name)}
            <div className="crm-avatar-ring" />
        </div>
    )
}

// ─── MODAL COMPONENT ─────────────────────────────────────────────────────────
const CRMModal = ({ visible, onClose, title, children, footer }) => {
    useEffect(() => {
        const fn = (e) => e.key === 'Escape' && onClose()
        if (visible) document.addEventListener('keydown', fn)
        return () => document.removeEventListener('keydown', fn)
    }, [visible, onClose])
    if (!visible) return null
    return (
        <div className="crm-modal-overlay" onClick={(e) => e.target.classList.contains('crm-modal-overlay') && onClose()}>
            <div className="crm-modal">
                <div className="crm-modal-header">
                    <span className="crm-modal-title">{title}</span>
                    <button className="crm-modal-close" onClick={onClose}><XIcon size={16} /></button>
                </div>
                <div className="crm-modal-body">{children}</div>
                {footer && <div className="crm-modal-footer">{footer}</div>}
            </div>
        </div>
    )
}

// ─── PAGINATION ──────────────────────────────────────────────────────────────
const Pagination = ({ page, total, onPage }) => {
    if (total <= 1) return null
    return (
        <div className="crm-pagination">
            <button className="crm-page-btn" onClick={() => onPage(1)} disabled={page === 1}>
                «
            </button>
            <button className="crm-page-btn" onClick={() => onPage(page - 1)} disabled={page === 1}>
                <ChevronLeftIcon />
            </button>
            <span style={{ padding:'0 10px', fontSize:'0.8rem', fontWeight:700, color:'var(--crm-text2)', whiteSpace:'nowrap' }}>
        {page} / {total}
      </span>
            <button className="crm-page-btn" onClick={() => onPage(page + 1)} disabled={page === total}>
                <ChevronRightIcon />
            </button>
            <button className="crm-page-btn" onClick={() => onPage(total)} disabled={page === total}>
                »
            </button>
        </div>
    )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const VaultCRM = () => {
    const { user } = useAuth()

    // ── State
    const [view,             setView]             = useState('list') // 'list' | 'detail'
    const [customers,        setCustomers]         = useState([])
    const [searchTerm,       setSearchTerm]        = useState('')
    const [selectedCustomer, setSelectedCustomer]  = useState(null)
    const [customerData,     setCustomerData]      = useState(null)
    const [loadingList,      setLoadingList]       = useState(true)
    const [loadingDetails,   setLoadingDetails]    = useState(false)
    const [error,            setError]             = useState(null)
    const [editingNoteId,    setEditingNoteId]     = useState(null)
    const [editNoteValue,    setEditNoteValue]     = useState('')
    const [modal,            setModal]             = useState({ visible:false, type:null, data:null, error:null })
    const [noteText,         setNoteText]          = useState('')
    const [isSubmitting,     setIsSubmitting]      = useState(false)
    const [isSavingNote,     setIsSavingNote]      = useState(false)
    const [alert,            setAlert]             = useState(null)
    const [currentPage,      setCurrentPage]       = useState(1)
    const CUSTOMERS_PER_PAGE = 9

    // ── Fetch list
    const fetchCustomers = useCallback(async () => {
        try {
            setLoadingList(true)
            setError(null)
            const res = await usersAPI.getCustomers()
            if (res.success) setCustomers(res.customers || [])
            else throw new Error('Müşteri listesi yüklenemedi.')
        } catch (err) {
            setError(err.message || 'Bir hata oluştu.')
        } finally {
            setLoadingList(false)
        }
    }, [])

    useEffect(() => { fetchCustomers() }, [fetchCustomers])

    // ── Filtering & pagination
    const filteredCustomers = useMemo(() => {
        const q = searchTerm.toLowerCase()
        return customers.filter(c =>
            (c.name    && c.name.toLowerCase().includes(q)) ||
            (c.surname && c.surname.toLowerCase().includes(q)) ||
            (c.phone   && c.phone.includes(q))
        )
    }, [customers, searchTerm])

    const paginatedCustomers = useMemo(() => {
        const start = (currentPage - 1) * CUSTOMERS_PER_PAGE
        return filteredCustomers.slice(start, start + CUSTOMERS_PER_PAGE)
    }, [filteredCustomers, currentPage])

    const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE))

    // ── Fetch detail
    const fetchCustomerDetails = async (customerId) => {
        try {
            setLoadingDetails(true)
            setError(null)
            setCustomerData(null)
            const res = await api.get(`/customer-followup/${customerId}`)
            if (res.success) setCustomerData(res.data)
            else throw new Error('Müşteri detayları yüklenemedi.')
        } catch (err) {
            setError(err.message || 'Bir hata oluştu.')
        } finally {
            setLoadingDetails(false)
        }
    }

    // ── Navigation
    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer)
        setView('detail')
        fetchCustomerDetails(customer.id)
    }

    const handleBackToList = () => {
        setView('list')
        setSelectedCustomer(null)
        setCustomerData(null)
        setError(null)
        setAlert(null)
    }

    // ── Note update (inline edit)
    const handleUpdateNote = async (noteId) => {
        if (!editNoteValue.trim()) return
        setIsSavingNote(true)
        try {
            const res = await api.put(`/appointment-notes/${noteId}`, { note: editNoteValue })
            if (res.success) {
                if (selectedCustomer) fetchCustomerDetails(selectedCustomer.id)
                setEditingNoteId(null)
                setEditNoteValue('')
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Not güncellenirken hata oluştu.')
        } finally {
            setIsSavingNote(false)
        }
    }

    // ── Modal
    const clearAlert = () => setAlert(null)

    const openModal = (type, data) => {
        setNoteText(type === 'general' ? data.general_notes || '' : '')
        setModal({ visible:true, type, data, error:null })
    }

    const closeModal = () => {
        setModal({ visible:false, type:null, data:null, error:null })
        setNoteText('')
        setIsSubmitting(false)
    }

    const handleModalSubmit = async () => {
        setIsSubmitting(true)
        setModal(prev => ({ ...prev, error:null }))
        try {
            if (modal.type === 'general') {
                await api.post(`/customer-profile/${selectedCustomer.id}`, {
                    general_notes: noteText,
                    preferences: modal.data.preferences,
                })
            } else if (modal.type === 'appointment') {
                await api.post('/appointment-notes', {
                    appointment_id: modal.data.id,
                    note: noteText,
                })
            }
            closeModal()
            setAlert({ type:'success', message:'Not başarıyla kaydedildi.' })
            fetchCustomerDetails(selectedCustomer.id)
            setTimeout(clearAlert, 3000)
        } catch (err) {
            setModal(prev => ({ ...prev, error: err.message || 'Kaydetme başarısız.' }))
        } finally {
            setIsSubmitting(false)
        }
    }

    // ── Note delete
    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Bu notu silmek istediğinizden emin misiniz?')) return
        try {
            await api.delete(`/appointment-notes/${noteId}`)
            setAlert({ type:'success', message:'Not silindi.' })
            fetchCustomerDetails(selectedCustomer.id)
            setTimeout(clearAlert, 3000)
        } catch (err) {
            setError(err.message || 'Not silinemedi.')
        }
    }

    // ── Derived stats (from detail)
    const detailStats = useMemo(() => {
        if (!customerData) return null
        const history = customerData.history || []
        const total   = history.reduce((s, a) => s + parseFloat(a.price || 0), 0)
        const count   = history.length
        const last    = count > 0 ? formatDate(history[0]?.appointment_date) : '-'
        const noteCount = history.reduce((s, a) => s + (a.notes?.length || 0), 0)
        return { total, count, last, noteCount }
    }, [customerData])

    // ── List stats (header)
    const listStats = useMemo(() => ({
        total: customers.length,
        found: filteredCustomers.length,
    }), [customers, filteredCustomers])

    // ─── RENDER: LIST ──────────────────────────────────────────────────────────
    const renderListView = () => (
        <div style={{ animation:'crm-fade-in 0.3s ease' }}>
            {/* Header */}
            <div className="crm-header-card">
                <div className="crm-header-left">
                    <div className="crm-header-icon">
                        <UserIcon size={20} />
                    </div>
                    <div>
                        <h1 className="crm-header-title">Müşteri Takibi (CRM)</h1>
                        <p className="crm-header-sub">Müşterilerinizin geçmişini, notlarını ve alışkanlıklarını yönetin.</p>
                    </div>
                </div>
            </div>

            {/* Stats */}
            {!loadingList && (
                <div className="crm-stats-row">
                    {[
                        { label:'Toplam Müşteri', value: listStats.total,   color:'var(--crm-accent)',  bg:'var(--crm-accent-l)',  icon:<UserIcon size={18}/> },
                        { label:'Arama Sonucu',   value: listStats.found,   color:'var(--crm-success)', bg:'var(--crm-success-l)', icon:<SearchIcon/> },
                    ].map((s, i) => (
                        <div className="crm-stat-card" key={i}>
                            <div className="crm-stat-icon" style={{ background:s.bg, color:s.color }}>{s.icon}</div>
                            <div>
                                <p className="crm-stat-label">{s.label}</p>
                                <p className="crm-stat-value">{s.value}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Main card */}
            <div className="crm-main-card">
                {/* Toolbar */}
                <div className="crm-toolbar">
                    <div className="crm-search-wrap">
                        <SearchIcon />
                        <input
                            className="crm-search"
                            placeholder="İsim, soyisim veya telefon ile ara..."
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }}
                        />
                    </div>
                    {!loadingList && (
                        <span className="crm-result-count">{filteredCustomers.length} müşteri</span>
                    )}
                </div>

                {/* Content */}
                {loadingList ? (
                    <div className="crm-customer-grid">
                        {[...Array(9)].map((_, i) => (
                            <div className="crm-skel-card" key={i}>
                                <div className="crm-skeleton" style={{ width:64, height:64, borderRadius:'50%' }} />
                                <div className="crm-skeleton" style={{ width:120, height:14 }} />
                                <div className="crm-skeleton" style={{ width:80, height:11 }} />
                                <div className="crm-skeleton" style={{ width:70, height:20, borderRadius:20 }} />
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="crm-alert crm-alert-danger" style={{ margin:20 }}>
                        <WarnIcon /> {error}
                    </div>
                ) : filteredCustomers.length === 0 ? (
                    <div className="crm-empty">
                        <div className="crm-empty-icon"><UserIcon size={28} /></div>
                        <h3 className="crm-empty-title">
                            {customers.length === 0 ? 'Henüz müşteri yok.' : 'Müşteri bulunamadı.'}
                        </h3>
                        <p className="crm-empty-sub">
                            {customers.length === 0
                                ? 'Sisteme kayıtlı müşteri bulunmuyor.'
                                : `"${searchTerm}" için sonuç bulunamadı.`}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="crm-customer-grid">
                            {paginatedCustomers.map((customer, idx) => (
                                <div
                                    key={customer.id}
                                    className="crm-customer-card"
                                    onClick={() => handleSelectCustomer(customer)}
                                    style={{ animationDelay: `${idx * 0.04}s` }}
                                    role="button" tabIndex={0}
                                    onKeyDown={e => e.key === 'Enter' && handleSelectCustomer(customer)}
                                >
                                    <CustomerAvatar name={customer.name} />
                                    <p className="crm-customer-name">{customer.name} {customer.surname}</p>
                                    {customer.phone && (
                                        <p className="crm-customer-phone">
                                            <PhoneIcon /> {customer.phone}
                                        </p>
                                    )}
                                    <span className="crm-customer-tag">Profili Gör →</span>
                                </div>
                            ))}
                        </div>
                        <Pagination page={currentPage} total={totalPages} onPage={setCurrentPage} />
                    </>
                )}
            </div>
        </div>
    )

    // ─── RENDER: DETAIL ────────────────────────────────────────────────────────
    const renderDetailView = () => (
        <div style={{ animation:'crm-fade-in 0.3s ease' }}>
            {/* Back */}
            <button className="crm-back-btn" onClick={handleBackToList}>
                <ArrowLeftIcon /> Tüm Müşterilere Dön
            </button>

            {/* Alert */}
            {alert && (
                <div className={`crm-alert crm-alert-${alert.type}`}>
                    <CheckIcon size={16} />
                    <span style={{ flex:1 }}>{alert.message}</span>
                    <button className="crm-alert-close" onClick={clearAlert}><XIcon size={14} /></button>
                </div>
            )}
            {error && (
                <div className="crm-alert crm-alert-danger">
                    <WarnIcon /> {error}
                    <button className="crm-alert-close" onClick={() => setError(null)}><XIcon size={14} /></button>
                </div>
            )}

            {loadingDetails ? (
                <div className="crm-detail-loader">
                    <div className="crm-detail-spinner" />
                    <p className="crm-detail-spinner-text">Müşteri profili yükleniyor...</p>
                </div>
            ) : !customerData ? (
                <div className="crm-alert crm-alert-warn" style={{ marginTop:8 }}>
                    <WarnIcon /> Müşteri verisi bulunamadı.
                </div>
            ) : (
                <>
                    {/* ── PROFILE HERO ── */}
                    <div className="crm-profile-hero">
                        <div className="crm-profile-hero-inner">
                            {/* Avatar */}
                            <div className="crm-profile-avatar">
                                {getInitials(selectedCustomer.name)}
                            </div>
                            {/* Info */}
                            <div className="crm-profile-info">
                                <h2 className="crm-profile-name">
                                    {selectedCustomer.name} {selectedCustomer.surname}
                                </h2>
                                <p className="crm-profile-phone">
                                    <PhoneIcon /> {selectedCustomer.phone || '-'}
                                </p>
                                <p className="crm-profile-notes-label">Genel Notlar &amp; Tercihler</p>
                                <div style={{ minHeight:'2.5em' }}>
                                    <NoteListRenderer
                                        text={customerData.profile.general_notes}
                                        emptyMessage="Genel not eklenmemiş..."
                                    />
                                </div>
                            </div>
                            {/* Edit */}
                            <button
                                className="crm-profile-edit-btn"
                                onClick={() => openModal('general', customerData.profile)}
                                title="Genel notları düzenle"
                            >
                                <PencilIcon size={16} />
                            </button>
                        </div>
                    </div>

                    {/* ── DETAIL STATS ── */}
                    {detailStats && (
                        <div className="crm-detail-stats">
                            {[
                                { label:'Toplam Ziyaret',    value: detailStats.count,                color:'var(--crm-accent)',  bg:'var(--crm-accent-l)',  icon:<CalendarIcon size={16}/> },
                                { label:'Toplam Harcama',    value: formatCurrency(detailStats.total),color:'var(--crm-success)', bg:'var(--crm-success-l)', icon:<TrendUpIcon/> },
                                { label:'Son Ziyaret',       value: detailStats.last,                 color:'var(--crm-info)',    bg:'var(--crm-info-l)',    icon:<ClockIcon/> },
                                { label:'Toplam Not',        value: detailStats.noteCount,            color:'var(--crm-warn)',    bg:'var(--crm-warn-l)',    icon:<MessageIcon size={16}/> },
                            ].map((s, i) => (
                                <div className="crm-detail-stat" key={i} style={{ animationDelay:`${i*0.06}s` }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                                        <div style={{ width:28, height:28, borderRadius:8, background:s.bg, color:s.color, display:'flex', alignItems:'center', justifyContent:'center' }}>{s.icon}</div>
                                        <p className="crm-detail-stat-label">{s.label}</p>
                                    </div>
                                    <p className="crm-detail-stat-val">{s.value}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── TIMELINE ── */}
                    <div className="crm-section-title">
                        <div className="crm-section-title-icon"><CalendarIcon size={15} /></div>
                        İşlem Geçmişi
                    </div>

                    {customerData.history.length === 0 ? (
                        <div className="crm-alert crm-alert-info">
                            <CalendarIcon size={16} />
                            <span>Bu müşteri için henüz tamamlanmış bir randevu bulunmuyor.</span>
                        </div>
                    ) : (
                        <div className="crm-timeline">
                            {customerData.history.map((app, idx) => {
                                const dateObj = app.appointment_date ? new Date(app.appointment_date) : null
                                const day   = dateObj ? dateObj.toLocaleDateString('tr-TR', { day:'2-digit' }) : '-'
                                const month = dateObj ? dateObj.toLocaleDateString('tr-TR', { month:'short' }) : ''

                                return (
                                    <div className="crm-timeline-item" key={app.id} style={{ animationDelay:`${idx*0.06}s` }}>
                                        {/* Timeline dot */}
                                        <div className="crm-timeline-dot">
                                            <span className="crm-timeline-dot-day">{day}</span>
                                            <span className="crm-timeline-dot-month">{month}</span>
                                        </div>

                                        {/* Appointment card */}
                                        <div className="crm-appt-card">
                                            {/* Header */}
                                            <div className="crm-appt-header">
                                                <div>
                                                    <p className="crm-appt-service">{app.service?.name || 'Hizmet'}</p>
                                                    <p className="crm-appt-price">{formatCurrency(app.price)}</p>
                                                </div>
                                                <span className="crm-badge-done">✓ Tamamlandı</span>
                                            </div>

                                            {/* Notes */}
                                            <div className="crm-appt-body">
                                                {app.notes?.length === 0 ? (
                                                    <div className="crm-no-notes">
                                                        <MessageIcon size={15} />
                                                        Bu randevu için not eklenmemiş.
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {app.notes.map(note => (
                                                            <div className="crm-note-item" key={note.id}>
                                                                {editingNoteId === note.id ? (
                                                                    /* EDIT MODE */
                                                                    <div className="crm-note-edit">
                                                                        <input
                                                                            className="crm-note-input"
                                                                            type="text"
                                                                            value={editNoteValue}
                                                                            onChange={e => setEditNoteValue(e.target.value)}
                                                                            autoFocus
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Enter')  handleUpdateNote(note.id)
                                                                                if (e.key === 'Escape') setEditingNoteId(null)
                                                                            }}
                                                                        />
                                                                        <div className="crm-note-edit-btns">
                                                                            <button
                                                                                className="crm-btn crm-btn-success crm-btn-sm"
                                                                                onClick={() => handleUpdateNote(note.id)}
                                                                                disabled={isSavingNote}
                                                                            >
                                                                                {isSavingNote ? <SpinnerIcon size={13}/> : <CheckIcon size={13}/>}
                                                                                Kaydet
                                                                            </button>
                                                                            <button
                                                                                className="crm-btn crm-btn-ghost crm-btn-sm"
                                                                                onClick={() => setEditingNoteId(null)}
                                                                            >
                                                                                <XIcon size={12}/> İptal
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    /* VIEW MODE */
                                                                    <>
                                                                        <div
                                                                            className="crm-note-content"
                                                                            style={{ cursor:'pointer' }}
                                                                            onClick={() => { setEditingNoteId(note.id); setEditNoteValue(note.note) }}
                                                                        >
                                                                            <NoteListRenderer text={note.note} emptyMessage="Not içeriği boş." />
                                                                            <p className="crm-note-staff">
                                                                                <UserIcon size={11} /> {note.staff?.name || 'Bilinmeyen Personel'}
                                                                            </p>
                                                                        </div>
                                                                        <div className="crm-note-actions">
                                                                            <button
                                                                                className="crm-btn-icon"
                                                                                title="Düzenle"
                                                                                onClick={() => { setEditingNoteId(note.id); setEditNoteValue(note.note) }}
                                                                            >
                                                                                <PencilIcon size={12} />
                                                                            </button>
                                                                            <button
                                                                                className="crm-btn-icon del"
                                                                                title="Sil"
                                                                                onClick={() => handleDeleteNote(note.id)}
                                                                            >
                                                                                <TrashIcon size={12} />
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Add note */}
                                            <div className="crm-appt-footer">
                                                <button
                                                    className="crm-add-note-btn"
                                                    onClick={() => openModal('appointment', app)}
                                                >
                                                    <PlusIcon /> Bu Randevuya Yeni Not Ekle
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    )

    // ─── RENDER: MODAL ────────────────────────────────────────────────────────
    const renderModal = () => (
        <CRMModal
            visible={modal.visible}
            onClose={closeModal}
            title={modal.type === 'general' ? 'Genel Notları Düzenle' : 'Randevu Notu Ekle'}
            footer={
                <>
                    <button className="crm-btn crm-btn-ghost" onClick={closeModal} disabled={isSubmitting}>
                        İptal
                    </button>
                    <button className="crm-btn crm-btn-primary" onClick={handleModalSubmit} disabled={isSubmitting}>
                        {isSubmitting ? <SpinnerIcon size={15} /> : <CheckIcon size={15} />}
                        Kaydet
                    </button>
                </>
            }
        >
            {/* Modal error */}
            {modal.error && (
                <div className="crm-alert crm-alert-danger" style={{ marginBottom:14 }}>
                    <WarnIcon /> {modal.error}
                </div>
            )}

            {/* Appointment info banner */}
            {modal.type === 'appointment' && modal.data && (
                <div className="crm-modal-appt-info">
                    <span className="crm-modal-appt-icon"><CalendarIcon size={20} /></span>
                    <div>
                        <p className="crm-modal-appt-name">{modal.data?.service?.name}</p>
                        <p className="crm-modal-appt-date">{formatDate(modal.data?.appointment_date)}</p>
                    </div>
                </div>
            )}

            {/* Textarea */}
            <label className="crm-form-label">
                {modal.type === 'general' ? 'Genel Notlar ve Tercihler' : 'Teknik Not'}
            </label>
            <textarea
                className="crm-textarea"
                rows={5}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder={
                    modal.type === 'general'
                        ? 'Müşterinin sevdiği içecekler, hassasiyetleri, sevdiği/sevmediği konular...'
                        : 'Uygulanan boya formülü, kesim detayı, bir sonraki sefere hatırlatma...'
                }
            />
            <p className="crm-helper-text">
                <MessageIcon size={12} />
                Yeni bir madde (yuvarlak top) için 'Enter' tuşuna basın.
            </p>
        </CRMModal>
    )

    // ─── MAIN RETURN ──────────────────────────────────────────────────────────
    return (
        <div className="crm-page">
            <GlobalStyles />
            {view === 'list' ? renderListView() : renderDetailView()}
            {renderModal()}
        </div>
    )
}

export default VaultCRM